import { useState } from "react";
import { fromHex } from "viem";
import {
    erc20ABI,
    useContractRead,
    useContractWrite,
    useWalletClient,
} from "wagmi";
import { waitForTransaction } from "wagmi/actions";
import { isEnabled } from "../ethers/tokens";
import {
    APPROVE_FUNCTION_NAME,
    CONFIRMATIONS_COUNT,
    // APPROVE_MAX_VALUE,
    ERROR_ALLOWANCE_APPROVE,
    ERROR_ALLOWANCE_FETCH,
    ERROR_ALLOWANCE_WRITE,
    INFLATE_PERCENT,
    USDT_TOKEN_NAME,
} from "../utils/config";

import { _notEmpty } from "../utils";
import { useGasEstimate } from "./useGasEstimate";
import { USDT_ERC20 } from "../ethers/abis";

export const useFarmTokenAllowance = ({
    config,
    nftId,
    token,
    userAccount,
    amount,
    isReset = false,
    farmContract
}) => {
    const { data: signer } = useWalletClient();
    const { chainId } = config;

    const contract = farmContract;

    const [allowance, setAllowance] = useState(null);
    const [isFetching, setIsFetching] = useState(true);

    const isUSDT = token.name === USDT_TOKEN_NAME;

    const enabled = isEnabled({
        config,
        nftId,
        token,
        allowNullNftId: true,
        bypassContractChecking: true
    }) && _notEmpty(userAccount);

    useContractRead({
        chainId,
        address: token?.address,
        abi: erc20ABI,
        functionName: "allowance",
        args: [
            userAccount,
            contract?.address
        ],
        watch: true,
        enabled,
        onError: (error) => {
            console.error(
                ERROR_ALLOWANCE_FETCH,
                error
            );
        },
        onSuccess: (data) => {
            setAllowance(data);
            setIsFetching(false);
        }
    });

    const approveContract = {
        address: token?.address,
        abi: isUSDT
            ? USDT_ERC20
            : erc20ABI
    };

    const approveArgs = [
        contract?.address,
        isReset
            ? 0
            : amount
    ];

    const {
        gasLimit,
        estimatedGas,
        estimatesGasFormatted,
        estimatedGasUSD,
        isError
    } = useGasEstimate({
        contract: approveContract,
        functionName: APPROVE_FUNCTION_NAME,
        args: approveArgs,
        account: signer?.account,
        amount
    });

    let gasLimitFormatted = parseInt(
        parseInt(gasLimit) * INFLATE_PERCENT
    ).toString();

    const tokenContract = useContractWrite({
        chainId,
        address: approveContract.address,
        abi: approveContract.abi,
        signerOrProvider: signer,
        functionName: APPROVE_FUNCTION_NAME,
        args: approveArgs,
        gas: gasLimitFormatted,
        enabled,
        onError: (error) => {
            console.error(
                ERROR_ALLOWANCE_WRITE,
                error
            );
        }
    });

    const approve = async ({
        onConfirmation,
        onApprovalComplete,
        onApproving,
        onError
    }) => {
        try {

            onConfirmation?.();

            const approveTransaction = await tokenContract.writeAsync();
            const { hash } = approveTransaction;

            onApproving?.(
                hash
            );

            const approve = await waitForTransaction({
                hash,
                confirmations: CONFIRMATIONS_COUNT,
                timeout: isUSDT ? 60000 : undefined
            });
            const { logs } = approve;

            const spendingCap = fromHex(
                logs[0].data,
                "bigint"
            );

            setAllowance(
                spendingCap
            );

            onApprovalComplete?.(
                approve,
                spendingCap,
                isReset
            );

            return true;

        } catch (error) {

            console.error(
                ERROR_ALLOWANCE_APPROVE,
                error
            );

            onError?.(error);
            return false;
        }
    };

    return {
        approve,
        allowance,
        isFetching,
        estimatedGas,
        estimatesGasFormatted,
        estimatedGasUSD,
        estimateHasError: isError
    };
};
